import { GTMProvider } from "@elgorditosalsero/react-gtm-hook";
import {
  MantineProvider,
  ColorSchemeProvider,
  ColorScheme,
} from "@mantine/core";
import React, { useState } from "react";
import { QueryClientProvider } from "react-query";
import { queryClient } from "../QueryClient";
import { GlobalStatusProvider } from "../GlobalStore";
import { initializeApp } from "firebase/app";
import { getPerformance } from "firebase/performance";
import { firebaseConfig } from "../firebase-config";
import { getAnalytics } from "firebase/analytics";
import { HelmetProvider } from "react-helmet-async";
import MainRouter from "./MainRouter";
import { getRemoteConfig } from "firebase/remote-config";
import { UpdatePopup } from "../UpdatePopup";

// const MainRouter = React.lazy(
//   () => import(/* webpackChunkName: "MainRouter" */ "./MainRouter")
// );

function App() {
  const [colorScheme, setColorScheme] = useState<ColorScheme>(
    (localStorage.getItem("colorScheme") as ColorScheme) ?? "dark"
  );
  const toggleColorScheme = (value?: ColorScheme) => {
    const newColorScheme = value || (colorScheme === "dark" ? "light" : "dark");
    localStorage.setItem("colorScheme", newColorScheme);
    setColorScheme(newColorScheme);
  };

  const app = initializeApp(firebaseConfig);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const analytics = getAnalytics(app);
  // Initialize Performance Monitoring and get a reference to the service
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const perf = getPerformance(app);
  window.remoteConfig = getRemoteConfig(app);
  window.remoteConfig.settings.minimumFetchIntervalMillis = 0; // 1 hour

  return (
    <HelmetProvider>
      <GTMProvider
        state={{
          id: process.env.REACT_APP_GTM_ID || "",
          injectScript: process.env.REACT_APP_ENV === "prd",
        }}
      >
        <ColorSchemeProvider
          colorScheme={colorScheme}
          toggleColorScheme={toggleColorScheme}
        >
          <MantineProvider
            withGlobalStyles
            withNormalizeCSS
            theme={{
              colorScheme,
              fontFamily: "Verdana, sans-serif",
              fontFamilyMonospace: "Monaco, Courier, monospace",
              headings: { fontFamily: "Verdana, sans-serif" },
              colors: {
                EigenPhiGreen: [
                  "#D9F7F0",
                  "#B4EFE1",
                  "#8EE7D3",
                  "#69DFC4",
                  "#44D7B6",
                  "#54B498",
                  "#44957D",
                  "#367764",
                  "#28594B",
                  "#1B3B32",
                ],
              },
              primaryColor: "EigenPhiGreen",
              components: {
                Anchor: {
                  styles: (theme) => ({
                    root: {
                      color:
                        theme.colorScheme === "dark"
                          ? theme.white
                          : theme.colors.dark[6],
                    },
                  }),
                },
                Menu: {
                  styles: (theme) => ({
                    root: { display: "flex" },
                    item: {
                      "&:hover": {
                        backgroundColor: "rgba(68,215,182,0.10) !important",
                        color: "#44D7B6",
                      },
                    },
                    itemLabel: {
                      width: "100%",
                    },
                    dropdown: {
                      boxShadow:
                        theme.colorScheme === "dark"
                          ? "0px 4px 12px 0px #000000"
                          : "0px 4px 12px 0px #e9ecef",
                      borderRadius: 8,
                    },
                  }),
                },
                Tabs: {
                  styles: (theme) => ({
                    tabsList: {
                      position: "relative",
                      zIndex: 100,
                    },
                  }),
                },
                Modal: {
                  styles: (theme) => ({
                    root: {
                      zIndex: 201,
                    },
                  }),
                },
              },
            }}
          >
            <QueryClientProvider client={queryClient}>
              <GlobalStatusProvider>
                {/* <Suspense fallback={<ArbitrageScanSkeleton />}>
                  <MainRouter colorScheme={colorScheme} />
                </Suspense> */}
                <MainRouter colorScheme={colorScheme} />
              </GlobalStatusProvider>
            </QueryClientProvider>
            <UpdatePopup />
          </MantineProvider>
        </ColorSchemeProvider>
      </GTMProvider>
    </HelmetProvider>
  );
}

export default App;
